import React from "react";
import { useSearchParams, Navigate, useLocation } from "react-router-dom";
import { Container, Card, Form, Button, Alert, Spinner } from "react-bootstrap";
import { ApiHelper, LoginDepartmentInterface, UserHelper, ToastHelper } from "../helpers";

interface DeviceInfo {
  userCode: string;
  clientId: string;
  scopes: string;
  expiresIn: number;
  error?: string;
}

interface ApproveResponse {
  success?: boolean;
  message?: string;
  error?: string;
}

export const DeviceAuthPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [userCode, setUserCode] = React.useState(searchParams.get("code") || "");
  const [deviceInfo, setDeviceInfo] = React.useState<DeviceInfo | null>(null);
  const [selectedDepartment, setSelectedDepartment] = React.useState<number | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState(false);
  const [step, setStep] = React.useState<'code' | 'confirm'>('code');

  // If not authenticated, redirect to login with state for return
  if (!ApiHelper.isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const departments = UserHelper.departments || [];

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Normalize code (remove hyphens, uppercase)
      const normalizedCode = userCode.replace(/-/g, '').toUpperCase();

      const result = await ApiHelper.get<DeviceInfo>(`/oauth/device/pending/${normalizedCode}`);

      if (result && !result.error) {
        setDeviceInfo(result);
        setStep('confirm');
        if (departments.length === 1) {
          setSelectedDepartment(departments[0].id);
        }
      } else {
        setError("Invalid or expired code. Please check and try again.");
      }
    } catch (err) {
      setError("Failed to verify code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedDepartment) {
      setError("Please select a department");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await ApiHelper.post<ApproveResponse>("/oauth/device/approve", {
        user_code: userCode,
        department_id: selectedDepartment
      });

      if (result.success) {
        setSuccess(true);
        ToastHelper.success("Device authorized successfully!");
      } else {
        setError(result.message || "Failed to authorize device");
      }
    } catch (err) {
      setError("Authorization failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeny = async () => {
    setLoading(true);
    try {
      await ApiHelper.post("/oauth/device/deny", { user_code: userCode });
      setError("Device authorization denied");
      setStep('code');
      setDeviceInfo(null);
      setUserCode("");
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const clean = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 6);
    setUserCode(clean);
  };

  if (success) {
    return (
      <div id="loginPage">
        <Container className="py-5">
          <Card className="mx-auto" style={{ maxWidth: 500 }}>
            <Card.Body className="text-center py-5">
              <div className="text-success mb-3">
                <i className="bi bi-check-circle-fill" style={{ fontSize: '4rem' }}></i>
                <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                </svg>
              </div>
              <h3>Device Authorized!</h3>
              <p className="text-muted">
                You can now return to your TV. The device will connect automatically.
              </p>
            </Card.Body>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <div id="loginPage">
      <Container className="py-5">
        <Card className="mx-auto" style={{ maxWidth: 500 }}>
          <Card.Header>
            <h4 className="mb-0">Authorize Device</h4>
          </Card.Header>
          <Card.Body>
            {error && <Alert variant="danger">{error}</Alert>}

            {step === 'code' && (
              <Form onSubmit={handleCodeSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Enter the code displayed on your TV:</Form.Label>
                  <Form.Control
                    type="text"
                    value={userCode}
                    onChange={handleCodeChange}
                    placeholder="XXXXXX"
                    maxLength={6}
                    style={{ fontSize: '1.5rem', letterSpacing: '0.2em', textAlign: 'center', fontFamily: 'monospace' }}
                    autoFocus
                  />
                </Form.Group>
                <Button
                  type="submit"
                  variant="primary"
                  className="w-100"
                  disabled={loading || userCode.length < 6}
                >
                  {loading ? <Spinner size="sm" animation="border" /> : "Continue"}
                </Button>
              </Form>
            )}

            {step === 'confirm' && deviceInfo && (
              <>
                <Alert variant="info">
                  <strong>Authorizing device code:</strong> {deviceInfo.userCode}
                  <br />
                  <small>Expires in {Math.floor(deviceInfo.expiresIn / 60)} minutes</small>
                </Alert>

                <Form.Group className="mb-3">
                  <Form.Label>Select Department:</Form.Label>
                  <Form.Select
                    value={selectedDepartment || ''}
                    onChange={(e) => setSelectedDepartment(Number(e.target.value))}
                  >
                    <option value="">Choose a department...</option>
                    {departments.map((dept: LoginDepartmentInterface) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <div className="mb-3">
                  <strong>Requested Permissions:</strong>
                  <ul className="mb-0">
                    {deviceInfo.scopes.split(' ').map(scope => (
                      <li key={scope}>{scope}</li>
                    ))}
                  </ul>
                </div>

                <div className="d-flex gap-2">
                  <Button
                    variant="success"
                    className="flex-grow-1"
                    onClick={handleApprove}
                    disabled={loading || !selectedDepartment}
                  >
                    {loading ? <Spinner size="sm" animation="border" /> : "Authorize Device"}
                  </Button>
                  <Button
                    variant="outline-danger"
                    onClick={handleDeny}
                    disabled={loading}
                  >
                    Deny
                  </Button>
                </div>
              </>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};
